import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  AdminPanelSettings,
  ManageAccounts,
  Schedule as ScheduleIcon,
  Engineering,
  SupervisorAccount,
  Visibility,
} from '@mui/icons-material';

import { UserRole } from '../../types';
import type { User, UserActivityLog, UserCreateInput } from '../../types';

type UserFormValue = UserCreateInput | (Partial<User> & { password?: string });

interface UserFormDialogProps {
  open: boolean;
  mode: 'create' | 'edit';
  user?: User | null;
  loading?: boolean;
  error?: string | null;
  onClose: () => void;
  onSubmit: (value: UserFormValue) => void;
}

interface RoleDialogProps {
  open: boolean;
  user?: User | null;
  loading?: boolean;
  error?: string | null;
  onClose: () => void;
  onSubmit: (role: UserRole) => void;
}

interface PasswordDialogProps {
  open: boolean;
  user?: User | null;
  loading?: boolean;
  error?: string | null;
  onClose: () => void;
  onSubmit: (password: string) => void;
}

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  loading?: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

interface ActivityDialogProps {
  open: boolean;
  user?: User | null;
  logs: UserActivityLog[];
  loading?: boolean;
  onClose: () => void;
}

const roleOptions = Object.values(UserRole);

const getRoleIcon = (role: UserRole) => {
  switch (role) {
    case UserRole.ADMIN:
      return <AdminPanelSettings fontSize="small" />;
    case UserRole.MANAGER:
      return <ManageAccounts fontSize="small" />;
    case UserRole.SCHEDULER:
      return <ScheduleIcon fontSize="small" />;
    case UserRole.ENGINEER:
      return <Engineering fontSize="small" />;
    case UserRole.DIRECTOR:
      return <SupervisorAccount fontSize="small" />;
    case UserRole.VIEWER:
    default:
      return <Visibility fontSize="small" />;
  }
};

const roleLabel = (role: UserRole) => {
  switch (role) {
    case UserRole.ADMIN:
      return 'Администратор';
    case UserRole.MANAGER:
      return 'Менеджер';
    case UserRole.SCHEDULER:
      return 'Планировщик';
    case UserRole.ENGINEER:
      return 'Инженер';
    case UserRole.DIRECTOR:
      return 'Директор';
    case UserRole.VIEWER:
    default:
      return 'Наблюдатель';
  }
};

export const UserFormDialog: React.FC<UserFormDialogProps> = ({
  open,
  mode,
  user,
  loading = false,
  error,
  onClose,
  onSubmit,
}) => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState<UserRole>(UserRole.VIEWER);
  const [password, setPassword] = useState('');
  const [notes, setNotes] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;

    setUsername(user?.username || '');
    setEmail(user?.email || '');
    setFullName(user?.full_name || '');
    setRole(user?.role || UserRole.VIEWER);
    setPassword('');
    setNotes(user?.notes || '');
    setIsActive(user?.is_active ?? true);
    setLocalError(null);
  }, [open, user]);

  const title = mode === 'create' ? 'Создать пользователя' : 'Редактировать пользователя';
  const submitLabel = mode === 'create' ? 'Создать' : 'Сохранить';

  const handleSubmit = () => {
    const cleanUsername = username.trim();
    const cleanEmail = email.trim();
    const cleanFullName = fullName.trim();
    const cleanNotes = notes.trim();

    if (!cleanUsername || cleanUsername.length < 3) {
      setLocalError('Имя пользователя должно содержать минимум 3 символа');
      return;
    }
    if (!cleanEmail || !cleanEmail.includes('@')) {
      setLocalError('Укажите корректный email');
      return;
    }
    if (mode === 'create' && password.length < 8) {
      setLocalError('Пароль должен содержать минимум 8 символов');
      return;
    }

    setLocalError(null);

    if (mode === 'create') {
      onSubmit({
        username: cleanUsername,
        email: cleanEmail,
        password,
        role,
        full_name: cleanFullName || null,
        notes: cleanNotes || null,
        is_active: isActive,
      });
      return;
    }

    onSubmit({
      username: cleanUsername,
      email: cleanEmail,
      role,
      full_name: cleanFullName || null,
      notes: cleanNotes || null,
      is_active: isActive,
    });
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {(error || localError) && <Alert severity="error">{error || localError}</Alert>}
          <TextField
            label="Имя пользователя"
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            required
            autoFocus
            disabled={loading}
          />
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
            disabled={loading}
          />
          <TextField
            label="Полное имя"
            value={fullName}
            onChange={(event) => setFullName(event.target.value)}
            disabled={loading}
          />
          {mode === 'create' && (
            <TextField
              label="Пароль"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
              disabled={loading}
              helperText="Минимум 8 символов, backend дополнительно проверит сложность"
            />
          )}
          <FormControl fullWidth disabled={loading}>
            <InputLabel>Роль</InputLabel>
            <Select
              value={role}
              label="Роль"
              onChange={(event) => setRole(event.target.value as UserRole)}
            >
              {roleOptions.map((item) => (
                <MenuItem key={item} value={item}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {getRoleIcon(item)}
                    {roleLabel(item)}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Заметки"
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            multiline
            minRows={3}
            disabled={loading}
          />
          <FormControlLabel
            control={
              <Switch
                checked={isActive}
                onChange={(event) => setIsActive(event.target.checked)}
                disabled={loading}
              />
            }
            label="Активная учетная запись"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Отмена</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? 'Сохранение...' : submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const UserRoleDialog: React.FC<RoleDialogProps> = ({
  open,
  user,
  loading = false,
  error,
  onClose,
  onSubmit,
}) => {
  const [role, setRole] = useState<UserRole>(UserRole.VIEWER);

  useEffect(() => {
    if (open) setRole(user?.role || UserRole.VIEWER);
  }, [open, user]);

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>Изменить роль</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <Typography variant="body2" color="text.secondary">
            Пользователь: {user?.full_name || user?.username}
          </Typography>
          <FormControl fullWidth disabled={loading}>
            <InputLabel>Роль</InputLabel>
            <Select
              value={role}
              label="Роль"
              onChange={(event) => setRole(event.target.value as UserRole)}
            >
              {roleOptions.map((item) => (
                <MenuItem key={item} value={item}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {getRoleIcon(item)}
                    {roleLabel(item)}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Отмена</Button>
        <Button onClick={() => onSubmit(role)} variant="contained" disabled={loading}>
          {loading ? 'Сохранение...' : 'Сохранить'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const UserPasswordDialog: React.FC<PasswordDialogProps> = ({
  open,
  user,
  loading = false,
  error,
  onClose,
  onSubmit,
}) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setPassword('');
    setConfirmPassword('');
    setLocalError(null);
  }, [open]);

  const handleSubmit = () => {
    if (password.length < 8) {
      setLocalError('Пароль должен содержать минимум 8 символов');
      return;
    }
    if (password !== confirmPassword) {
      setLocalError('Пароли не совпадают');
      return;
    }
    setLocalError(null);
    onSubmit(password);
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>Сбросить пароль</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {(error || localError) && <Alert severity="error">{error || localError}</Alert>}
          <Typography variant="body2" color="text.secondary">
            Пользователь: {user?.full_name || user?.username}
          </Typography>
          <TextField
            label="Новый пароль"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoFocus
            disabled={loading}
          />
          <TextField
            label="Повторите пароль"
            type="password"
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
            disabled={loading}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Отмена</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? 'Сохранение...' : 'Сбросить'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const UserConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  title,
  message,
  confirmLabel,
  loading = false,
  onClose,
  onConfirm,
}) => (
  <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="xs">
    <DialogTitle>{title}</DialogTitle>
    <DialogContent>
      <Typography variant="body2">{message}</Typography>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} disabled={loading}>Отмена</Button>
      <Button onClick={onConfirm} variant="contained" color="error" disabled={loading}>
        {loading ? 'Выполнение...' : confirmLabel}
      </Button>
    </DialogActions>
  </Dialog>
);

export const UserActivityDialog: React.FC<ActivityDialogProps> = ({
  open,
  user,
  logs,
  loading = false,
  onClose,
}) => {
  const title = useMemo(() => (
    user ? `Активность: ${user.full_name || user.username}` : 'Активность пользователя'
  ), [user]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {loading && <LinearProgress sx={{ mb: 2 }} />}
        {!loading && logs.length === 0 && (
          <Alert severity="info">Для этого пользователя пока нет событий активности.</Alert>
        )}
        <List dense>
          {logs.map((log) => (
            <ListItem key={log.id} divider alignItems="flex-start">
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Typography variant="subtitle2">{log.event_type}</Typography>
                    <Chip label={log.level} size="small" />
                    <Typography variant="caption" color="text.secondary">
                      {new Date(log.timestamp).toLocaleString()}
                    </Typography>
                  </Box>
                }
                secondary={log.message}
              />
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Закрыть</Button>
      </DialogActions>
    </Dialog>
  );
};
